// Данные раздела «Агент» и «Решения» (apps/api/src/routes/agent.ts).
export interface RunReport {
  headline: string;
  summary: string;
  asks_owner: string[];
}

export interface FeedRun {
  id: string;
  threadId: string | null;
  kind: string;
  kindLabel: string;
  trigger: string;
  status:
    | 'queued'
    | 'deferred'
    | 'starting'
    | 'running'
    | 'completed'
    | 'interrupted'
    | 'failed'
    | 'cancelled';
  report: RunReport | null;
  model: string;
  effort: string | null;
  deferReason: string | null;
  deferredUntil: string | null;
  scheduledFor: string | null;
  attempts: number;
  costUsd: number;
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
  working: boolean;
  artifacts?: { notes: number; decisions: number; proposals: number; followups: number };
}

export interface QueueConfig {
  maxBackground: number;
  fiveHourMax: number;
  sevenDayMax: number;
  quiet: { from: string; to: string } | null;
  paused: boolean;
}

export interface ScheduleView {
  id: number;
  kind: string;
  title: string;
  cron: string;
  simple: { time: string; days: number[]; monthDay?: number } | null;
  enabled: boolean;
  prompt: string | null;
  lastFiredAt: string | null;
  nextAt: string | null;
}

export interface SchedulesResponse {
  schedules: ScheduleView[];
  followups: Array<{ id: number; dueAt: string; prompt: string; createdByRun: string | null }>;
  reliability: { total: number; ok: number };
}

export interface TaskView {
  id: number;
  title: string;
  description: string | null;
  status: 'queued' | 'running' | 'waiting_owner' | 'done' | 'failed' | 'cancelled';
  createdBy: 'owner' | 'agent';
  progress: string | null;
  result: string | null;
  threadId: string | null;
  createdAt: string;
  updatedAt: string;
  finishedAt: string | null;
  working: boolean;
}

export interface NoteView {
  id: number;
  /** owner — слово владельца; plan — договорились сделать или проверить. */
  kind: 'insight' | 'hypothesis' | 'rule' | 'owner' | 'plan';
  text: string;
  scope: { offer?: string; productType?: string; season?: string; geo?: string } | null;
  evidence: Array<{ text: string; ref?: string; at: string }>;
  confidence: number;
  status: 'active' | 'archived' | 'merged';
  archivedReason: string | null;
  mergedInto: number | null;
  confirmedCount: number;
  refutedCount: number;
  lastConfirmedAt: string | null;
  expiresAt: string | null;
  /** Срок плана; у остальных видов — null. */
  dueAt: string | null;
  createdBy: 'agent' | 'owner';
  createdAt: string;
  updatedAt: string;
}

export interface NoteEvent {
  id: number;
  type: string;
  actor: 'agent' | 'owner' | 'platform';
  data: Record<string, unknown> | null;
  createdAt: string;
}

export interface DigestView {
  id: number;
  period: 'day' | 'week' | 'month';
  periodStart: string;
  text: string;
  numbers: {
    since: string;
    until: string;
    spend: number;
    leads: number;
    approved: number;
    profit: number;
    forecastProfit: number;
    approveRate: number | null;
    cpl: number | null;
  } | null;
  updatedAt: string;
}

export interface Proposal {
  id: string;
  type: string;
  domain: string;
  title: string;
  target: { kind: string; id?: string; name?: string } | null;
  payload: Record<string, unknown> | null;
  reason: string;
  expectedEffect: string | null;
  evidence: { text?: string } | null;
  status: 'shadow' | 'pending' | 'approved' | 'executing' | 'rejected' | 'expired' | 'executed' | 'failed';
  /** Ответ шлюза: решение, режим, правило, рост дневных бюджетов (delta_usd), распределено (allocated_usd). */
  policy: Record<string, unknown> | null;
  /** Итог выполнения: done, error, undo (откат), undoneAt. */
  result: Record<string, unknown> | null;
  ownerVerdict: 'agree' | 'disagree' | null;
  ownerComment: string | null;
  decidedAt: string | null;
  runId: string | null;
  threadId: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface JournalEntry {
  id: number;
  actor: 'agent' | 'owner' | 'guard' | 'platform';
  domain: string;
  action: string;
  title: string;
  target: { kind: string; id?: string; name?: string } | null;
  reason: string | null;
  expectedEffect: string | null;
  reviewAt: string | null;
  outcome: string | null;
  verdict: 'worked' | 'failed' | 'unclear' | null;
  outcomeAt: string | null;
  createdAt: string;
  runId: string | null;
  proposal: { id: string; status: string; ownerVerdict: string | null; type: string } | null;
}

/** Почему работа ждёт — законченной фразой для человека. */
export function waitReason(r: Pick<FeedRun, 'status' | 'deferReason' | 'deferredUntil'>): string | null {
  const until = r.deferredUntil
    ? new Date(r.deferredUntil).toLocaleTimeString('ru-RU', {
        timeZone: 'Asia/Tashkent',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
  switch (r.deferReason) {
    case 'limit':
      return `Подписка близко к лимиту — продолжит${until ? ` в ${until}` : ' после сброса окна'}.`;
    case 'quiet':
      return `Тихие часы — начнёт${until ? ` в ${until}` : ' после них'}.`;
    case 'access':
      return 'Нет доступа к подписке Claude — продолжит сама, когда доступ вернётся.';
    case 'busy':
      return 'В очереди: агент занят другой работой.';
    case 'paused':
      return 'Агент на паузе — начнёт, когда снимете паузу в «Расписании».';
    case 'runner':
      return 'Ждёт, пока перезапустится сервис агента.';
    case 'update':
      return 'Ждёт конца обновления Claude Code.';
    case 'retry':
      return `Повтор после сбоя${until ? ` в ${until}` : ''}.`;
    case 'restart':
      return 'Продолжит после перезапуска.';
    default:
      return r.status === 'queued' ? 'В очереди.' : null;
  }
}
